// touch: 2026-05-04-inline-video-autoplay
(function () {
  'use strict';

  class TcGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.items = Array.from(this.querySelectorAll('[data-media-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.counter = this.querySelector('[data-counter-current]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.active = 0;
      this._pendingSource = null;
      if (!this.thumbs.length || !this.items.length) return;

      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i, null, 'thumb')));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, null, 'dot')));
      if (this.prevBtn) this.prevBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.go(this.active - 1, -1, 'button'); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); this.go(this.active + 1, 1, 'button'); });

      // Zoom button: open the PhotoSwipe lightbox directly at the active
      // slide via the API, rather than simulating a click on the anchor
      // (delegated click handlers don't always fire from synthetic clicks).
      this.zoomBtn = this.querySelector('[data-zoom-btn]');
      if (this.zoomBtn) {
        this.zoomBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          const lb = window.__tcPswpLightbox;
          if (lb && typeof lb.loadAndOpen === 'function') {
            lb.loadAndOpen(this.active);
            return;
          }
          // Fallback: synthesise a click on the active item's zoom link
          // (covers the rare case where PhotoSwipe hasn't initialised yet).
          const activeItem = this.items[this.active];
          const link = activeItem && activeItem.querySelector('.tc-gal__zoom-link');
          if (link) link.click();
        });
      }

      // Mobile uses native scroll-snap on .tc-gal__main — listen for scroll
      // settle and commit the new active index. No touch handlers needed;
      // the browser's scroll engine handles all the swipe physics.
      if (this.stage) {
        let scrollRaf = null;
        this.stage.addEventListener('scroll', () => {
          if (!this._isMobile()) return;
          if (scrollRaf) return;
          scrollRaf = requestAnimationFrame(() => {
            scrollRaf = null;
            const w = this.stage.clientWidth;
            if (!w) return;
            const idx = Math.round(this.stage.scrollLeft / w);
            const clamped = Math.max(0, Math.min(this.items.length - 1, idx));
            // Only commit on a settle (within 4px of a snap target) so
            // mid-flick rAF ticks don't thrash the active state.
            if (Math.abs(this.stage.scrollLeft - clamped * w) > 4) return;
            if (clamped !== this.active) this._setActive(clamped, this._pendingSource || 'swipe');
          });
        }, { passive: true });
      }

      this.onKey = (e) => {
        if (!this.isInView) return;
        if (e.key === 'ArrowLeft') { this.go(this.active - 1, -1, 'keyboard'); e.preventDefault(); }
        if (e.key === 'ArrowRight') { this.go(this.active + 1, 1, 'keyboard'); e.preventDefault(); }
      };
      document.addEventListener('keydown', this.onKey);

      this.isInView = false;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          const wasInView = this.isInView;
          this.isInView = entries[0].isIntersecting;
          // First time the gallery enters the viewport, kick playback on
          // the active slide if it's a video. Subsequent slide changes
          // are handled by _setActive().
          if (this.isInView && !wasInView) this._playActiveVideo();
        }, { threshold: 0.1 });
        this.io.observe(this);
      } else {
        this.isInView = true;
        this._playActiveVideo();
      }
    }

    _playActiveVideo() {
      const active = this.items[this.active];
      const v = active && active.querySelector('video');
      if (!v) return;
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.then(() => { v.dataset.playing = 'true'; })
         .catch(() => { delete v.dataset.playing; });
      } else {
        v.dataset.playing = 'true';
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      if (this.io) this.io.disconnect();
    }

    _isMobile() {
      return window.matchMedia('(max-width: 920px)').matches;
    }

    go(idx, direction, source) {
      const n = this.items.length;
      if (!n) return;
      const next = ((idx % n) + n) % n;
      if (next === this.active) return;

      if (this._isMobile()) {
        // Mobile: scroll the snap container; the scroll listener commits
        // the active state once it settles. Stash the source so the
        // listener can attribute the change correctly.
        if (this.stage && typeof this.stage.scrollTo === 'function') {
          const w = this.stage.clientWidth;
          if (w) {
            this._pendingSource = source || 'unknown';
            this.stage.scrollTo({ left: next * w, behavior: 'smooth' });
            return;
          }
        }
      }
      // Desktop (or mobile fallback if stage.scrollTo unavailable): commit
      // synchronously. direction is preserved for analytics.
      this._setActive(next, source || 'unknown', direction);
    }

    _setActive(next, source, direction) {
      if (next === this.active) return;
      const from = this.active;
      const cur = this.items[from];
      const inc = this.items[next];

      // ---- Inline video auto-play on swipe ----
      // Outgoing slide: pause + rewind any video (so the next time the
      // user swipes back to this slide, playback restarts from frame 0).
      // Drop the data-playing flag so the ▶ overlay reappears.
      const outVid = cur && cur.querySelector('video');
      if (outVid) {
        if (!outVid.paused) outVid.pause();
        try { outVid.currentTime = 0; } catch (_) { /* no-op if metadata not ready */ }
        delete outVid.dataset.playing;
      }
      // Incoming slide: if it's a video, kick playback. preload="none" on
      // the element means the browser hasn't downloaded a byte yet —
      // calling .play() triggers the load + play. Catch AbortError that
      // fires if the user swipes away mid-load.
      const inVid = inc && inc.querySelector('video');
      if (inVid) {
        try { inVid.currentTime = 0; } catch (_) { /* no-op */ }
        const p = inVid.play();
        if (p && typeof p.catch === 'function') {
          p.then(() => { inVid.dataset.playing = 'true'; })
           .catch(() => { delete inVid.dataset.playing; });
        } else {
          inVid.dataset.playing = 'true';
        }
      }

      // is-active drives display swap on desktop; on mobile (scroll-snap)
      // it's informational only but still updated for parity.
      if (cur) cur.classList.remove('is-active');
      if (inc) inc.classList.add('is-active');

      if (this.thumbs[from]) {
        this.thumbs[from].classList.remove('is-active');
        this.thumbs[from].setAttribute('aria-selected', 'false');
      }
      if (this.thumbs[next]) {
        const thumb = this.thumbs[next];
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-selected', 'true');
        // Center the active thumb in the rail. On mobile the rail is
        // display:none (offsetParent === null) so the scroll is skipped.
        const rail = thumb.closest('.tc-gal__rail');
        if (rail && rail.offsetParent !== null && typeof rail.scrollTo === 'function') {
          const railRect = rail.getBoundingClientRect();
          const thumbRect = thumb.getBoundingClientRect();
          const thumbInRail = (thumbRect.left - railRect.left) + rail.scrollLeft;
          const target = thumbInRail - (rail.clientWidth / 2) + (thumb.clientWidth / 2);
          const max = rail.scrollWidth - rail.clientWidth;
          rail.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
        }
      }

      if (this.dots[from]) {
        this.dots[from].classList.remove('is-active');
        this.dots[from].setAttribute('aria-selected', 'false');
      }
      if (this.dots[next]) {
        this.dots[next].classList.add('is-active');
        this.dots[next].setAttribute('aria-selected', 'true');
      }

      this.active = next;
      this._pendingSource = null;
      if (this.counter) this.counter.textContent = String(next + 1);

      // Unified change event — tc-pdp-tracking.js translates this into the
      // appropriate GA4 event (pdp_gallery_next, prev, thumb) using `source`.
      const dir = direction != null ? direction : (next > from ? 1 : -1);
      this.dispatchEvent(new CustomEvent('tc:gallery-change', {
        bubbles: true,
        detail: { from: from, to: next, source: source || 'unknown', direction: dir }
      }));
    }
  }

  class TcQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      this.btns = Array.from(this.querySelectorAll('[data-delta]'));
      if (!this.input) return;
      const MIN = parseInt(this.input.min || '1', 10);
      const MAX = parseInt(this.input.max || '10', 10);
      const clamp = (v) => Math.max(MIN, Math.min(MAX, v));
      const emit = () => this.dispatchEvent(new CustomEvent('tc:qty-change', {
        detail: { value: parseInt(this.input.value, 10) || MIN },
        bubbles: true
      }));
      this.btns.forEach((b) => b.addEventListener('click', () => {
        const d = parseInt(b.dataset.delta, 10) || 0;
        this.input.value = clamp((parseInt(this.input.value, 10) || MIN) + d);
        emit();
      }));
      this.input.addEventListener('change', () => {
        this.input.value = clamp(parseInt(this.input.value, 10) || MIN);
        emit();
      });
    }
  }

  class TcBuyButton extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.btn = this.querySelector('button[type="submit"]');
      this.priceSpan = this.querySelector('.tc-btn__price');
      this.labelSpan = this.querySelector('.tc-btn__label');
      this.originalLabel = this.labelSpan ? this.labelSpan.innerHTML : '';
      this.basePriceCents = null;

      if (this.form) {
        this.form.addEventListener('tc:qty-change', (e) => this.updateTotal(e.detail.value));
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }
    }

    get basePrice() {
      if (this.basePriceCents != null) return this.basePriceCents;
      const txt = this.priceSpan ? this.priceSpan.textContent.replace(/[^0-9.]/g, '') : '';
      const v = parseFloat(txt);
      this.basePriceCents = Number.isFinite(v) ? Math.round(v * 100) : 0;
      return this.basePriceCents;
    }

    updateTotal(qty) {
      if (!this.priceSpan) return;
      const cents = this.basePrice * (qty || 1);
      const dollars = (cents / 100).toFixed(2);
      const sym = (this.priceSpan.dataset.sym || '$');
      this.priceSpan.textContent = sym + dollars;
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.form || !this.btn) return;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData(this.form);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }
      this.btn.disabled = true;
      try {
        const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const response = await res.json();
        if (response.status) throw new Error(response.description || 'Cart add failed');
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }
        if (this.labelSpan) this.labelSpan.textContent = '✓ Added to cart';
        setTimeout(() => {
          if (this.labelSpan) this.labelSpan.innerHTML = this.originalLabel;
          this.btn.disabled = false;
        }, 1500);
      } catch (err) {
        this.btn.disabled = false;
        this.form.submit();
      }
    }
  }

  class TcAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll(':scope > details'));
      this.defaultOpen = parseInt(this.getAttribute('data-default-open') != null ? this.getAttribute('data-default-open') : '-1', 10);

      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      this.items.forEach((d, i) => {
        if (isDesktop && this.defaultOpen === i) d.open = true;
        else d.open = false;
        // Keep .is-open class in sync with [open] (source CSS uses .is-open for caret rotation).
        d.classList.toggle('is-open', d.open);
      });

      this.items.forEach((d) => {
        d.addEventListener('toggle', () => {
          // Sync .is-open class with native [open] state.
          d.classList.toggle('is-open', d.open);
          if (!d.open) return;
          if (!window.matchMedia('(min-width: 1024px)').matches) return;
          this.items.forEach((other) => {
            if (other !== d && other.open) {
              other.open = false;
              other.classList.remove('is-open');
            }
          });
        });
      });
    }
  }

  function initDiscountCapture() {
    const wrapper = document.querySelector('.tc-discount-capture');
    if (!wrapper) return;
    const form = wrapper.querySelector('.tc-discount-capture__form');
    const msg = wrapper.querySelector('.tc-discount-capture__msg');
    const successTpl = wrapper.querySelector('[data-success-tpl]');
    const companyId = wrapper.dataset.klaviyoCompanyId;
    const listId = wrapper.dataset.klaviyoListId;
    const discountCode = wrapper.dataset.discountCode || '';
    const redirectPath = wrapper.dataset.redirectPath || '/cart';
    if (!form) return;

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input[name="email"]');
      const btn = form.querySelector('button');
      const email = (input.value || '').trim();
      msg.className = 'tc-discount-capture__msg';
      msg.textContent = '';

      if (!EMAIL_RE.test(email)) {
        msg.textContent = 'Please enter a valid email address.';
        msg.className = 'tc-discount-capture__msg is-error';
        input.focus();
        return;
      }

      if (!companyId || !listId) {
        msg.textContent = 'Offer not configured — please enter the Klaviyo list ID in theme settings.';
        msg.className = 'tc-discount-capture__msg is-error';
        return;
      }

      btn.disabled = true;
      const originalBtnText = btn.textContent;
      btn.textContent = 'Sending…';

      try {
        const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email: email } } },
                custom_source: 'PDP first-order discount'
              },
              relationships: { list: { data: { type: 'list', id: listId } } }
            }
          })
        });

        if (res.status >= 200 && res.status < 300) {
          msg.textContent = '';
          msg.className = 'tc-discount-capture__msg';
          form.style.display = 'none';
          const claimBtn = wrapper.querySelector('[data-claim-btn]');
          if (claimBtn) claimBtn.hidden = false;
          // Notify the tracking script (PII guard: send the domain only).
          try {
            const at = email.indexOf('@');
            const domain = at >= 0 ? email.slice(at + 1).toLowerCase() : '';
            document.dispatchEvent(new CustomEvent('tc:discount-submit-success', {
              detail: { email_domain: domain }
            }));
          } catch (_) { /* tracking is best-effort */ }
        } else {
          throw new Error('Klaviyo ' + res.status);
        }
      } catch (err) {
        msg.textContent = 'Something went wrong. Please try again in a moment.';
        msg.className = 'tc-discount-capture__msg is-error';
        btn.disabled = false;
        btn.textContent = originalBtnText;
      }
    });

    const claimBtn = wrapper.querySelector('[data-claim-btn]');
    if (claimBtn) {
      claimBtn.addEventListener('click', async () => {
        claimBtn.disabled = true;
        const originalText = claimBtn.textContent;
        claimBtn.textContent = 'Applying…';
        try {
          if (discountCode) {
            // Visit /discount/CODE to set the discount cookie. Redirect target is
            // small and same-origin; the cookie is set via Set-Cookie regardless.
            await fetch('/discount/' + encodeURIComponent(discountCode) + '?redirect=/cart.js', {
              method: 'GET',
              credentials: 'same-origin'
            }).catch(() => {});
          }
          const mainAdd = document.querySelector('.tc-buy-button button[type="submit"]');
          if (mainAdd && !mainAdd.disabled) {
            mainAdd.click();
          } else {
            window.location.href = '/discount/' + encodeURIComponent(discountCode) + '?redirect=' + encodeURIComponent(redirectPath);
            return;
          }
        } finally {
          setTimeout(() => {
            claimBtn.disabled = false;
            claimBtn.textContent = originalText;
          }, 1800);
        }
      });
    }
  }

  function initReviewsAnchor() {
    // Wire smooth-scroll on every reviews link/affordance: the desktop
    // single-line link AND the mobile trio cell.
    const links = document.querySelectorAll(
      '.tc-info__reviews a[href="#shopify-product-reviews"], ' +
      'a.tc-info__trio-cell--reviews[href="#shopify-product-reviews"]'
    );
    if (!links.length) return;
    const handler = (e) => {
      const target =
        document.querySelector('.jdgm-rev-widg') ||
        document.querySelector('.jdgm-widget') ||
        document.querySelector('#jdgm-review-widget') ||
        document.querySelector('[id^="shopify-section"][id*="judgeme"]');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    links.forEach((link) => link.addEventListener('click', handler));
  }

  function initStickyBuy() {
    const bar = document.getElementById('tc-stickybuy');
    const row = document.querySelector('.tc-buy-row');
    if (!bar || !row) return;

    const inlineInput = document.querySelector('.tc-form input[name="quantity"]');
    const stickyInput = bar.querySelector('[data-sticky-qty]');
    const stickyQtyBtns = bar.querySelectorAll('[data-sticky-qty-delta]');

    const QMIN = 1;
    const QMAX = stickyInput ? (parseInt(stickyInput.max, 10) || 10) : 10;
    const clampQ = (v) => Math.max(QMIN, Math.min(QMAX, v));

    // Push the sticky bar's qty into the inline form so when the sticky CTA
    // delegates to the inline submit button, the right quantity goes through.
    const writeInline = (v) => {
      if (!inlineInput) return;
      inlineInput.value = v;
      inlineInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    if (stickyInput) {
      stickyQtyBtns.forEach((b) => {
        b.addEventListener('click', () => {
          const d = parseInt(b.dataset.stickyQtyDelta, 10) || 0;
          const next = clampQ((parseInt(stickyInput.value, 10) || QMIN) + d);
          stickyInput.value = next;
          writeInline(next);
        });
      });
      stickyInput.addEventListener('change', () => {
        const next = clampQ(parseInt(stickyInput.value, 10) || QMIN);
        stickyInput.value = next;
        writeInline(next);
      });
    }

    const mq = window.matchMedia('(max-width: 767px)');
    let rowVisible = true;

    const sync = () => {
      if (!mq.matches) { bar.classList.remove('is-visible'); return; }
      const becomeVisible = !rowVisible;
      bar.classList.toggle('is-visible', becomeVisible);
      // When the sticky bar is about to show, pull the latest qty from the
      // inline form so the two stay in sync if the user touched the inline
      // stepper before scrolling past it.
      if (becomeVisible && stickyInput && inlineInput) {
        stickyInput.value = clampQ(parseInt(inlineInput.value, 10) || QMIN);
      }
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        rowVisible = entries[0].isIntersecting;
        sync();
      }, { threshold: 0 });
      io.observe(row);
    } else {
      bar.classList.remove('is-visible');
    }

    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);

    const stickyBtn = bar.querySelector('[data-sticky-add]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        // Belt-and-suspenders: re-sync qty right before submit.
        if (stickyInput) writeInline(clampQ(parseInt(stickyInput.value, 10) || QMIN));
        const inlineBtn = document.querySelector('.tc-buy-button button[type="submit"]');
        if (inlineBtn) inlineBtn.click();
      });
    }
  }

  if (!customElements.get('tc-gal')) customElements.define('tc-gal', TcGallery);
  if (!customElements.get('tc-qty')) customElements.define('tc-qty', TcQty);
  if (!customElements.get('tc-buy-button')) customElements.define('tc-buy-button', TcBuyButton);
  if (!customElements.get('tc-accordion')) customElements.define('tc-accordion', TcAccordion);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initStickyBuy(); initReviewsAnchor(); initDiscountCapture(); });
  } else {
    initStickyBuy();
    initReviewsAnchor();
    initDiscountCapture();
  }
})();
